import { api } from '@/services/api/AxiosBaseService'
import { createCrudService } from '@/services/api/createCrudService'

export interface CollaboratorDependent {
  id?: string
  name: string
  cpf: string | null
  birthDate: string | null
  degreeRelationshipId: string | null
  gender: string | null
  status: 'ATIVO' | 'INATIVO'
  /** Número da carteirinha (só leitura, vem do backend). */
  membershipCardNumber?: string | null
  formatted?: { membershipCardNumber?: string }
}

export interface Collaborator {
  id: string
  code: number
  name: string
  socialName: string | null
  cpf: string | null
  rg: string | null
  birthDate: string | null
  planId: string | null
  companyBranchId: string | null
  companyDepartmentId: string | null
  maritalStatusId: string | null
  discountCouponId: string | null
  coupon: string | null
  discountCoupon?: {
    id: string
    code: string
    discountType: 'VALOR FIXO' | 'PORCENTAGEM'
    value: number
    monthlyInstallments: number | null
  } | null
  email: string | null
  phone: string | null
  mobile: string | null
  gender: string | null
  status: 'ATIVO' | 'INATIVO'
  address?: {
    postalCode: string | null
    address: string | null
    number: string | null
    complement: string | null
    district: string | null
    city?: { id: string; city: string; state?: { id: string; abbreviation: string } | null } | null
  }
  membershipCardNumber: string | null
  plan?: { id: string; name: string } | null
  companyBranch?: { id: string; systemName: string | null; corporateName?: string | null } | null
  companyDepartment?: { id: string; name: string } | null
  dependents?: CollaboratorDependent[]
  formatted?: { cpf?: string; createdAt?: string; membershipCardNumber?: string; inactivatedAt?: string | null }
}

/** Grau de parentesco permitido para o plano (do pacote do produto). */
export interface PlanEligibility {
  degreeRelationshipId: string
  degreeRelationship: string | null
  minimumAge: number | null
}
export interface ReleasedPlan {
  id: string
  name: string
  price: number
  planType: string | null
  eligibilities: PlanEligibility[]
}

/** Cupom disponível para o plano de um titular (aplicação em lote). */
export interface BatchCouponOption {
  id: string
  code: string
  discountType: 'VALOR FIXO' | 'PORCENTAGEM'
  value: number
  monthlyInstallments: number | null
  formatted?: { value?: string }
}
/** Titular candidato à aplicação de cupom em lote (sem cupom, com plano). */
export interface BatchCouponHolder {
  id: string
  code: number
  name: string
  planId: string | null
  planName: string | null
  createdAt: string | null
  formatted?: { createdAt?: string }
  availableCoupons: BatchCouponOption[]
}

const base = createCrudService<Collaborator>('/my-company/collaborators', { searchField: 'name' })

export const CollaboratorsService = {
  ...base,
  /** Planos liberados para a empresa (com regras de elegibilidade por grau). */
  async releasedPlans(): Promise<ReleasedPlan[]> {
    const r = await api.get<{ data: ReleasedPlan[] }>('/my-company/collaborators/released-plans')
    return r.data.data
  },
  /** Valida um cupom por código para um plano (visão da empresa). null se inválido. */
  async findCoupon(code: string, planId: string): Promise<BatchCouponOption | null> {
    try {
      const r = await api.get<{ data: BatchCouponOption }>('/my-company/collaborators/find-coupon', {
        params: { code, plan_id: planId },
      })
      return r.data.data
    } catch {
      return null
    }
  },
  /** Titulares sem cupom (com plano) + cupons válidos para cada plano. */
  async couponBatchCandidates(): Promise<BatchCouponHolder[]> {
    const r = await api.get<{ data: BatchCouponHolder[] }>('/my-company/collaborators/coupon-batch-candidates')
    return r.data.data
  },
  /** Aplica os cupons escolhidos, em lote. */
  async batchApplyCoupon(items: { collaborator_id: string; discount_coupon_id: string }[]): Promise<{ applied: number; skipped: number }> {
    const r = await api.post<{ data: { applied: number; skipped: number } }>('/my-company/collaborators/batch-apply-coupon', { items })
    return r.data.data
  },
}
